// Service Worker policy.
//
// PWA in a browser: register the SW for offline support — that's the
// whole point. PWA inside a Capacitor wrapper (iOS/Android native
// app): NEVER register a SW. Capacitor already serves files locally
// from the app bundle, so the SW provides zero benefit AND actively
// harms the user — the registered SW caches every chunk and then
// keeps serving the cached chunks forever, even after the on-disk
// public/ folder has been refreshed via `cap sync ios`. From the
// user's perspective: every rebuild lands on disk but never actually
// runs, because the SW intercepts the request and returns the stale
// cached response from before. This was the root cause of the
// "ничего не изменилось после переустановки" bug (2026-06-25).
//
// Detection: `(window as any).Capacitor?.isNativePlatform?.()` is
// set by the Capacitor runtime on iOS/Android, never in a plain
// browser. We also defensively unregister any previously-installed
// SW + delete all Cache Storage entries — that flushes the legacy
// cache that the user already has on their phone from the first
// install, so future loads come from the on-disk bundle directly.
const isNative = (() => {
    try {
        const cap = (window as any).Capacitor;
        return !!(cap && (cap.isNativePlatform ? cap.isNativePlatform() : cap.platform && cap.platform !== 'web'));
    } catch { return false; }
})();

if (isNative) {
    // Best-effort flush — never block the boot if any step fails.
    (async () => {
        try {
            if ('serviceWorker' in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map((r) => r.unregister()));
            }
            if (typeof caches !== 'undefined' && caches.keys) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k)));
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[boot] SW/cache flush failed', err);
        }
    })();
} else {
    // Web only — register SW for offline support. registerType:'autoUpdate'
    // обновляет ассеты в фоне и применяет их при СЛЕДУЮЩЕЙ навигации — БЕЗ
    // принудительной перезагрузки. Принудительный reload (был тут раньше) мог
    // перезагрузить вкладку посреди долгой операции (пере-синтез схемы) и бросить
    // опрос результата. Свежий бандл подхватывается на следующем заходе/обновлении.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    import('virtual:pwa-register').then(({ registerSW }) => {
        registerSW({ onNeedRefresh() {}, onOfflineReady() {} });
    }).catch(() => { /* ignore */ });
}

// Build stamp lives in console only (no visible pill — was obscuring
// the UI). Open Safari Web Inspector to see it.
declare const __BUILD_TS__: string;
// eslint-disable-next-line no-console
console.info('[boot] build', __BUILD_TS__);

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import './assets/styles/index.css';
import '@unia/task-ui/style.css';
import './lib/noteAssistantSetup';
import '@unia/diagram-canvas/style.css';
import { installKeyboardSync } from './lib/keyboardSync';
import { installCaretRepaint } from './lib/caretRepaint';

// Wire native keyboard → CSS variable BEFORE first paint so the chat input
// dock is glued to the keyboard from the very first show event.
void installKeyboardSync();
// Курсор в поле — на месте после сдвига окна над клавиатурой (27.10.2026).
installCaretRepaint();

createRoot(document.getElementById('root')!).render(
  <StrictMode><App /></StrictMode>
);
